/**
 * Applicant Form_Renderer — shared pure-module bindings
 * (application-form-builder spec, task 23.1).
 *
 * The renderer's structural decisions (phase gating, per-division visibility,
 * audience membership) and its value shapes MUST be the *same code* the server
 * runs, so client and server never drift. Following the precedent set by
 * `deriveZodSchema.ts` and `mockApplicationFormClient.ts`, this module reaches
 * directly into the I/O-free `infrastructure/lambda/application-form/*` (and
 * `division-registry/*`) pure modules via a relative cross-import and re-exports
 * exactly what the renderer needs. Those modules pull only the pure `FieldType`
 * / `SubmissionStatus` enums and `division-registry` logic, so they bundle
 * cleanly under Vite/Vitest — no AWS SDK, no Node-only dependency.
 *
 * Centralising the cross-imports here keeps the `../../../../../infrastructure`
 * relative path in one place and lets the renderer components import from a
 * local, intention-revealing barrel.
 */

// --- Enums (runtime values) -------------------------------------------------
export { FieldType, SubmissionStatus } from '../../../../../infrastructure/types/database';

// --- Shared value + snapshot types ------------------------------------------
export type {
  Audience,
  BindingTarget,
  FieldCategory,
  FieldValidationCode,
  FieldValidationError,
  FileRef,
  FormFieldDef,
  FormVersionSnapshot,
  Phase,
  ResponseValue,
  SnapshotPage,
} from '../../../../../infrastructure/lambda/application-form/fieldValidation';

// --- Field/response validation (Req 5.2, 5.3, 5.4, 5.5, 5.6) ----------------
// The renderer's inline messaging delegates to the SAME pure rules the server
// runs — it never re-implements a validation decision. `validateValue` powers
// the per-field affordances (LONG_TEXT limits, FILE_UPLOAD constraints) and
// `validateResponse` powers the submission-block summary of unanswered required
// visible fields, so client messaging and server validation never drift.
export {
  validateResponse,
  validateValue,
} from '../../../../../infrastructure/lambda/application-form/fieldValidation';

// --- Phase gating (Req 16.2, 16.3, 16.4) ------------------------------------
export {
  availablePages,
  isPhaseTwoUnlocked,
} from '../../../../../infrastructure/lambda/application-form/phaseGating';

// --- Per-division visibility (Req 4.2, 4.3, 4.4, 4.5) -----------------------
export { isFieldVisibleForDivision } from '../../../../../infrastructure/lambda/application-form/formStructure';
export { isVisibleForDivision } from '../../../../../infrastructure/lambda/division-registry/visibility';

// --- Audience membership half of Effective_Visibility (Req 22) --------------
export {
  isEffectivelyVisible,
  isInAudience,
} from '../../../../../infrastructure/lambda/application-form/audienceVisibility';

// --- Division read-model ----------------------------------------------------
export type { Division } from '../../../../../infrastructure/lambda/division-registry/registryLogic';
export type { ApplicantType } from '../../../../../infrastructure/lambda/division-registry/resolution';

// --- Bound-field rules (Req 17, 18) -----------------------------------------
// Bound controls render canonical-entity-backed inputs that store NOTHING in
// the response `values` map. The renderer delegates every bound decision to the
// SAME pure rules the server runs — division options/validity, the locked
// contact/identity flag, and canonical resolution — so a bound control can
// never disagree with the server about what is selectable or locked.
export {
  divisionSelectorOptions,
  isContactIdentityLocked,
  resolveBoundField,
  validateDivisionSelection,
  writerForTarget,
} from '../../../../../infrastructure/lambda/application-form/boundField';
export type {
  CanonicalSources,
  CanonicalWriter,
  DivisionOption,
  DocumentRef,
  ResolvedBoundValue,
  ResolvedContact,
} from '../../../../../infrastructure/lambda/application-form/boundField';

// --- Ranked title preferences (Req 19) --------------------------------------
// Free-text ranked preferences with a NON-BLOCKING contention warning. The
// renderer normalizes/dedupes with the same rules the server persists, so the
// list the applicant sees equals the list that is stored.
export {
  capRankedPreferences,
  isValidPreference,
  normalizeRanked,
  titleContentionWarnings,
} from '../../../../../infrastructure/lambda/application-form/titlePreferences';
export type { TitlePreferences } from '../../../../../infrastructure/lambda/application-form/titlePreferences';

// --- Season selection (Req 4.2, 4.3, 4.7) -----------------------------------
// A Season Bound_Field's selectable options are exactly the Seasons whose
// application open-window includes the current instant. The renderer renders
// the same `SelectableSeason` shape the `selectSeason` write op validates
// against, so the control can never offer a Season the server would reject.
export type { SelectableSeason } from '../../../../../infrastructure/lambda/application-form/seasonSelection';

// --- Document catalog (Req 20) ----------------------------------------------
// The director-configured, per-competition catalog drives the uploader's
// in-scope slots and the required-document checklist/gate. `validateDocumentUpload`
// is the SAME pure, I/O-free file-constraint check the apply uploader runs before
// requesting a presigned URL (apply-document-uploads Req 5), so the client's
// pre-upload rejection can never disagree with the catalog's declared constraints.
// `effectivePhase` / `phaseLabel` derive and label a catalog entry's phase, and
// `isPhase2UploadDenied` is the authoritative server-side Phase-2 gate. The
// applicant renderer reuses the SAME pure helpers the backend ops and presign
// gate run, so the client Phase-2 hide and the "Phase 1"/"Phase 2" labels can
// never drift from the server (application-builder-documents Req 3, 4, 5).
export {
  effectivePhase,
  entriesInScope,
  isPhase2UploadDenied,
  missingRequiredDocuments,
  phaseLabel,
  validateDocumentUpload,
} from '../../../../../infrastructure/lambda/application-form/documentCatalog';
export type {
  CandidateUpload,
  DocumentCatalogEntry,
  DocumentPhase,
  UploadedDocument,
} from '../../../../../infrastructure/lambda/application-form/documentCatalog';

// --- Application-stage gate (member-onboarding-flow Req 4) ------------------
// The built-in Division_Selection_Entry vs Phase_1_Forms vs closed-window
// decision, and the exactly-one division-entry completion rule, are pure and
// total functions of derived state. The apply surface consults the SAME rules
// the server-side pure core exposes, so the built-in entry can never disagree
// with the server about which stage is active or which selection is acceptable.
export {
  resolveApplicationStage,
  resolveApplicantAppState,
  validateDivisionEntryCompletion,
} from '../../../../../infrastructure/lambda/application-form/applicationStage';
export type {
  ApplicantAppState,
  ApplicantAppStateInput,
  ApplicationStage,
  ApplicationStageState,
  ApplicationStatusValue,
  DivisionEntryCompletion,
  DivisionEntryRejectionCode,
} from '../../../../../infrastructure/lambda/application-form/applicationStage';

// --- Page-approval lock (Req 21.4) ------------------------------------------
// An approval-required Form_Page is locked to the applicant exactly once it is
// APPROVED. The container reuses the SAME pure predicate the server runs to
// decide editability, so the renderer's read-only lock can never disagree with
// the server about which pages are locked (independent of applicationStatus).
export { isPageEditableByApplicant } from '../../../../../infrastructure/lambda/application-form/pageApproval';
export type { PageApprovalStatus } from '../../../../../infrastructure/lambda/application-form/pageApproval';

// --- Applicant Season_Bound_Field suppression (apply-season-auto-derivation Req 5.1, 5.2) ---
// On the applicant surface the Season is auto-derived and never an applicant-facing
// selection, so a director-authored Season_Bound_Field (`bindingTarget === 'SEASON'`)
// must never be mapped to a control for an Applicant. This is a pure, total filter over
// the field definitions applied at the applicant renderer's field-mapping seam: it drops
// every SEASON-bound field and preserves every other field with its order and content
// unchanged. `SeasonBoundControl` itself is untouched, so `Director_Surfaces` keep
// rendering it (Req 8.1, 8.2). The filter is generic over the field shape so it composes
// with any list carrying a `bindingTarget`, returning a new array (never mutating input).
import type { BindingTarget as _BindingTarget } from '../../../../../infrastructure/lambda/application-form/fieldValidation';

/**
 * Drop every Season_Bound_Field (`bindingTarget === 'SEASON'`) from an applicant
 * field list, preserving all other fields in their original order and content.
 * Pure and total: never mutates its input and returns a new array.
 *
 * Validates: Requirements 5.1, 5.2
 */
export function suppressSeasonFields<F extends { bindingTarget?: _BindingTarget }>(
  fields: readonly F[],
): F[] {
  return fields.filter((field) => field.bindingTarget !== 'SEASON');
}
